<template>
    <div>
        <Toolbar>
            <template>
                <BackButton :to="__('navigation.dashboard')" :href="$route('dashboard')"/>
            </template>
            <template v-slot:right>
                <HelpButton/>
            </template>
        </Toolbar>

        <Card>
            <template v-slot:icon>
                <i class="fas fa-cogs"/>
            </template>
            <template v-slot:title>
                {{ __('ftp.title') }}
            </template>
            <template v-slot:description>
                {{ __('ftp.description') }}
            </template>

            <h2>{{ __('ftp.sub_title') }}</h2>

            <div class="mt-4">
                <div class="checkbox-inline my-4">
                    <label class="checkbox checkbox-lg">
                        <input type="checkbox" v-model="ownFtp">
                        <span></span> {{ __('ftp.label.description.own_store') }} </label>
                </div>
                <div class="form-group">
                    <label class="font-weight-bold">{{ __('ftp.label.host') }}</label>
                    <input type="text" class="form-control" placeholder="ftp.youbuyda.com" :disabled="!ownFtp"
                           v-model="host">
                    <span class="form-text text-muted">{{ __('ftp.label.description.host') }}</span>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group">
                            <label class="font-weight-bold">{{ __('ftp.label.user') }}</label>
                            <input type="text" class="form-control" v-model="ftpUser">
                            <span class="form-text text-muted">{{ __('ftp.label.description.user') }}</span>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group">
                            <label class="font-weight-bold">{{ __('ftp.label.password') }}</label>
                            <div class="input-group">
                                <input :type="showPassword ? 'text' : 'password'" class="form-control"
                                       :placeholder="!ownFtp ? 'Passwort wird generiert...' : 'Password eintragen oder leer lassen -> Password wird generiert'"
                                       v-model="password" :disabled="!ownFtp">
                                <div class="input-group-append">
                                    <button class="btn btn-secondary" type="button"
                                            @click="showPassword = !showPassword">
                                        <span class="fa-solid"
                                              :class="{ 'fa-eye' : !showPassword, 'fa-eye-slash' : showPassword}"></span>
                                    </button>
                                </div>
                            </div>

                            <span class="form-text text-muted">{{ __('ftp.label.description.password') }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="form-group" v-if="hasFtpAccount">
                <label class="font-weight-bold">{{ __('ftp.label.test') }}</label>
                <br>
                <button class="btn btn-xs btn-secondary px-5" :disabled="testing" @click="testConnection">
                    <i class="fas fa-arrow-right"/>
                    {{ __('actions.test_connection') }}
                </button>
                <div v-if="testing" class="text-warning mt-1">
                    <div class="spinner spinner-xs spinner-warning d-inline-block mr-5"/>
                    {{ __('ftp.alert.test.testing') }}
                </div>
                <div v-if="!testing && testingError !== ''" class="text-danger">
                    <i class="fas fa-exclamation-triangle"/>
                    {{ testingError }}
                </div>
                <div v-if="!testing && testingError === ''">
                    <div v-if="ftpAccountReady != null && ftpAccountReady" class="text-success mt-1">
                        <i class="fas fa-check-circle"/>
                        {{ __('ftp.alert.test.success') }}
                    </div>
                    <div v-else-if="ftpAccountReady != null && !ftpAccountReady" class="text-danger mt-1">
                        <i class="fas fa-times-circle"/>
                        {{ __('ftp.alert.test.fail') }}
                    </div>
                </div>
                <span v-if="files.length === 0" class="form-text text-muted">
                    {{ __('ftp.label.description.testing') }}
                </span>
                <div v-else>
                    <div class="font-weight-bold">{{ __('general.files') }}</div>
                    <ul>
                        <li v-for="file in files">{{ file.path }}</li>
                    </ul>
                </div>
            </div>
            <div class="form-group mt-6">
                <button class="btn btn-primary" @click="submitData">
                    <i class="fas fa-save"/>
                    {{ __('actions.submit') }}
                </button>
            </div>

            <template v-slot:footer>
                <Alert v-if="hasFtpAccount" :closable="false" :type="hasConnectors ? 'info' : 'warning'">
                    <template v-slot:icon>
                        <div class="alert-icon"><i class="fas fa-info-circle"></i></div>
                    </template>
                    <div class="d-flex flex-row justify-content-between">
                        <p>{{ __('ftp.alert.connector') }}</p>
                        <Link :href="$route('ftp.connector.settings')" class="btn btn-primary" as="button">
                            {{ hasConnectors ? __('actions.connectors_edit') : __('actions.connectors_create') }}
                        </Link>
                    </div>
                </Alert>
            </template>
        </Card>
    </div>
</template>

<script>
import { Link } from '@inertiajs/vue2';
import BackButton from "@components/BackButton.vue";
import Spinner from "vue-simple-spinner";
import Card from "@components/Cards/Card.vue";
import Alert from "@components/Alert.vue";
import HelpButton from '@components/HelpButton.vue'
import Toolbar from '@components/Toolbar.vue'

export default {
    components: {Toolbar, HelpButton, BackButton, Link, Card, Spinner, Alert},

    props: {
        ftpAccount: Object,
        stores: Array,
        hasConnectors: Boolean,
        hasFtpAccount: Boolean
    },
    data() {
        return {
            storeSettings: this.stores,
            testing: false,
            testingError: '',
            ftpAccountReady: null,
            host: "ftp.youbuyda.com",
            files: [],
            ftpDir: null,
            ftpUser: null,
            password: '',
            showPassword: true,
            ownFtp: false
        }
    },
    methods: {
        submitData() {
            if (this.hasFtpAccount) {
                this.updateAccountData()
            } else {
                this.saveUserSettings()
            }
        },

        saveUserSettings() {
            this.$inertia.post(this.$route('ftp.account.create'), {
                host: this.host,
                user: this.ftpUser,
                password: this.password,
                dir: this.ftpDir,
                own_ftp: this.ownFtp
            }, {
                preserveState: true,
                onSuccess: () => {
                    toastr.success(__('ftp.toast_message.submited'))
                },
                onError: (error) => {
                    toastr.error(error)
                },
            });
        },

        updateAccountData() {
            this.$inertia.post(this.$route('ftp.account.update'), {
                host: this.host,
                user: this.ftpUser,
                password: this.password,
                dir: this.ftpDir,
                own_ftp: this.ownFtp
            }, {
                preserveState: true,
                onSuccess: () => {
                    toastr.success(__('ftp.toast_message.updated'))
                },
                onError: (error) => {
                    toastr.error(error)
                },
            });
        },

        testConnection() {
            this.testing = true;

            axios.post(this.$route('ftp.account.test'), {
                host: this.host,
                user: this.ftpUser,
                password: this.password,
            }).then(response => {
                this.ftpAccountReady = response.data.success;
                this.testing = false;
                this.testingError = '';
                this.files = response.data.files;
            }).catch(() => {
                this.ftpAccountReady = false;
                this.testing = false;
                this.testingError = __('general.unknown_error');
                this.files = [];
            })
        },

        basename(path) {
            let pathParts = path.split("/")
            if (pathParts.length > 0) {
                return pathParts.pop();
            }

            return path;
        },
    },
    mounted() {
        if (this.hasFtpAccount) {
            this.showPassword = false;
            this.ftpUser = this.ftpAccount.username;
            this.password = this.ftpAccount.password;
            this.host = this.ftpAccount.host;
        }
    }
}
</script>
